/**
 * Poreski i matični brojevi firme po državi (JIB/MB, PIB, EU PDV broj). Ista pravila ima Go
 * (`server/internal/taxid`); oba testa čitaju `spec/fixtures/tax-ids.json`.
 *
 * - BA: JIB 13 cifara, PIB (PDV broj) 12 cifara.
 * - RS: matični broj 8 cifara, PIB 9 cifara sa kontrolnom cifrom (ISO 7064, MOD 11,10).
 * - ME: matični broj / PIB 8 cifara.
 * - HR: OIB 11 cifara sa kontrolnom cifrom (ISO 7064, MOD 11,10).
 * - ostalo: 5–15 cifara.
 *
 * Kodovi grešaka su ključevi `errors:validation.<code>`.
 */

export const COUNTRIES = ['BA', 'RS', 'ME', 'HR', 'other'] as const;
export type Country = (typeof COUNTRIES)[number];

export const EU_VAT_PREFIXES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'EL',
  'ES',
  'FI',
  'FR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
  'XI',
] as const;

export type TaxIdCode =
  | 'digitsOnly'
  | 'jibLength'
  | 'mbLength'
  | 'registrationLength'
  | 'pibLengthBA'
  | 'pibLengthRS'
  | 'pibLengthME'
  | 'pibLengthHR'
  | 'pibLength'
  | 'pibChecksum'
  | 'vatPrefix'
  | 'vatFormat';

export interface TaxIdResult {
  /** Normalizovana vrijednost (bez razmaka, tačaka, crtica i kosih crta). */
  value: string;
  /** `null` = ispravno (i za praznu vrijednost: polja su opciona). */
  code: TaxIdCode | null;
}

/** Labele polja po državi (i18n ključevi `panel:settings.org.taxIds.*`). */
export const TAX_ID_LABELS: Record<Country, { registration: string; pib: string }> = {
  BA: { registration: 'jib', pib: 'pibBA' },
  RS: { registration: 'mb', pib: 'pib' },
  ME: { registration: 'mb', pib: 'pib' },
  HR: { registration: 'mbs', pib: 'oib' },
  other: { registration: 'registration', pib: 'taxNumber' },
};

/** Očekivani broj cifara (za pomoć uz polje); `null` = 5–15. */
export const TAX_ID_LENGTHS: Record<Country, { registration: number | null; pib: number | null }> =
  {
    BA: { registration: 13, pib: 12 },
    RS: { registration: 8, pib: 9 },
    ME: { registration: 8, pib: 8 },
    HR: { registration: null, pib: 11 },
    other: { registration: null, pib: null },
  };

const DIGITS = /^[0-9]+$/;

export function normalizeTaxId(v: string | null | undefined): string {
  return (v ?? '').trim().replace(/[\s.\-/]/g, '');
}

export function normalizeVat(v: string | null | undefined): string {
  return normalizeTaxId(v).toUpperCase();
}

/** ISO 7064 MOD 11,10 (PIB Srbije, OIB Hrvatske): posljednja cifra je kontrolna. */
export function mod1110(d: string): boolean {
  if (d.length < 2 || !DIGITS.test(d)) return false;
  let p = 10;
  for (const ch of d.slice(0, -1)) {
    let s = (Number(ch) + p) % 10;
    if (s === 0) s = 10;
    p = (2 * s) % 11;
  }
  return (11 - p) % 10 === Number(d[d.length - 1]);
}

const isCountry = (c: string): c is Country => (COUNTRIES as readonly string[]).includes(c);

/** JIB (BA) ili matični broj. */
export function checkRegistration(country: string, v: string | null | undefined): TaxIdResult {
  const value = normalizeTaxId(v);
  if (!value) return { value, code: null };
  if (!DIGITS.test(value)) return { value, code: 'digitsOnly' };
  const c = isCountry(country) ? country : 'other';
  if (c === 'BA') return { value, code: value.length === 13 ? null : 'jibLength' };
  if (c === 'RS' || c === 'ME') return { value, code: value.length === 8 ? null : 'mbLength' };
  return { value, code: value.length >= 5 && value.length <= 15 ? null : 'registrationLength' };
}

/** PIB / PDV broj po državi. */
export function checkPib(country: string, v: string | null | undefined): TaxIdResult {
  const value = normalizeTaxId(v);
  if (!value) return { value, code: null };
  if (!DIGITS.test(value)) return { value, code: 'digitsOnly' };
  const c = isCountry(country) ? country : 'other';
  switch (c) {
    case 'BA':
      return { value, code: value.length === 12 ? null : 'pibLengthBA' };
    case 'RS':
      if (value.length !== 9) return { value, code: 'pibLengthRS' };
      return { value, code: mod1110(value) ? null : 'pibChecksum' };
    case 'ME':
      return { value, code: value.length === 8 ? null : 'pibLengthME' };
    case 'HR':
      if (value.length !== 11) return { value, code: 'pibLengthHR' };
      return { value, code: mod1110(value) ? null : 'pibChecksum' };
    default:
      return { value, code: value.length >= 5 && value.length <= 15 ? null : 'pibLength' };
  }
}

/** EU PDV broj: prefiks države članice (EL = Grčka, XI = Sjeverna Irska) + 2–12 slova ili cifara. */
export function checkVat(v: string | null | undefined): TaxIdResult {
  const value = normalizeVat(v);
  if (!value) return { value, code: null };
  const prefix = value.slice(0, 2);
  if (!/^[A-Z]{2}/.test(value) || !(EU_VAT_PREFIXES as readonly string[]).includes(prefix)) {
    return { value, code: 'vatPrefix' };
  }
  return { value, code: /^[A-Z0-9+*]{2,12}$/.test(value.slice(2)) ? null : 'vatFormat' };
}
